import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Input, Modal, Tooltip } from 'antd';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@shared/ui/antd';

import styles from './analytics-filters.module.scss';

const MAX_URL_FILTER_LENGTH = 2048;

type DraftUrl = {
  id: number;
  value: string;
};

type UrlFilterModalProps = {
  open: boolean;
  value: string[];
  onCancel: () => void;
  onApply: (values: string[]) => void;
};

const normalizeUrlPatterns = (values: string[]) =>
  Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));

export const UrlFilterModal: FC<UrlFilterModalProps> = ({ open, value, onCancel, onApply }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'analytics.filters.urlModal' });
  const nextIdRef = useRef(0);
  const [draftUrls, setDraftUrls] = useState<DraftUrl[]>([]);

  const createDraftUrl = useCallback(
    (url = ''): DraftUrl => ({
      id: nextIdRef.current++,
      value: url,
    }),
    [],
  );

  useEffect(() => {
    if (!open) return;
    setDraftUrls((value.length ? value : ['']).map((url) => createDraftUrl(url)));
  }, [createDraftUrl, open, value]);

  const totalLength = useMemo(
    () => draftUrls.reduce((total, item) => total + item.value.length, 0),
    [draftUrls],
  );
  const normalizedDraft = useMemo(
    () => normalizeUrlPatterns(draftUrls.map((item) => item.value)),
    [draftUrls],
  );
  const normalizedValue = useMemo(() => normalizeUrlPatterns(value), [value]);
  const isDirty = JSON.stringify(normalizedDraft) !== JSON.stringify(normalizedValue);

  const handleValueChange = (id: number, nextValue: string) => {
    setDraftUrls((current) => {
      const currentItem = current.find((item) => item.id === id);
      if (!currentItem) return current;

      const otherValuesLength =
        current.reduce((total, item) => total + item.value.length, 0) - currentItem.value.length;
      const availableLength = Math.max(0, MAX_URL_FILTER_LENGTH - otherValuesLength);

      return current.map((item) =>
        item.id === id ? { ...item, value: nextValue.slice(0, availableLength) } : item,
      );
    });
  };

  const handleAppendWildcard = (id: number) => {
    if (totalLength >= MAX_URL_FILTER_LENGTH) return;
    setDraftUrls((current) =>
      current.map((item) => (item.id === id ? { ...item, value: `${item.value}*` } : item)),
    );
  };

  const handleRemove = (id: number) => {
    setDraftUrls((current) => {
      const next = current.filter((item) => item.id !== id);
      return next.length ? next : [createDraftUrl()];
    });
  };

  const handleClear = () => {
    setDraftUrls([createDraftUrl()]);
  };

  return (
    <Modal
      centered
      open={open}
      width={520}
      footer={null}
      title={t('title')}
      className={styles.urlModal}
      onCancel={onCancel}
    >
      <Flex vertical gap={12}>
        <Text variant="sm" className={styles.urlModalDescription}>
          {t('description')}
        </Text>

        <Flex vertical gap={8} className={styles.urlRows}>
          {draftUrls.map((item) => (
            <Flex key={item.id} align="center" gap={8}>
              <Input
                value={item.value}
                aria-label={t('inputLabel')}
                className={styles.urlInput}
                onChange={(event) => handleValueChange(item.id, event.target.value)}
              />

              <Tooltip title={t('appendWildcard')}>
                <Button
                  aria-label={t('appendWildcard')}
                  className={styles.rowAction}
                  disabled={totalLength >= MAX_URL_FILTER_LENGTH}
                  onClick={() => handleAppendWildcard(item.id)}
                >
                  *
                </Button>
              </Tooltip>

              <Tooltip title={t('remove')}>
                <Button
                  aria-label={t('remove')}
                  icon={<CloseOutlined />}
                  className={styles.rowAction}
                  onClick={() => handleRemove(item.id)}
                />
              </Tooltip>
            </Flex>
          ))}
        </Flex>

        <Flex align="center" justify="space-between" gap={8} wrap="wrap">
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setDraftUrls((current) => [...current, createDraftUrl()])}
          >
            {t('add')}
          </Button>

          <Button size="small" type="text" disabled={totalLength === 0} onClick={handleClear}>
            {t('clear')}
          </Button>
        </Flex>

        <Text variant="xs" className={styles.characterCount}>
          {totalLength} / {MAX_URL_FILTER_LENGTH}
        </Text>

        <Flex justify="end" gap={8}>
          <Button size="small" onClick={onCancel}>
            {t('cancel')}
          </Button>
          <Button
            size="small"
            type="primary"
            disabled={!isDirty}
            onClick={() => onApply(normalizedDraft)}
          >
            {t('apply')}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
